<template>
  <transition name="fade" mode="out-in">
    <div class="col-span-full pb-8 sm:col-span-full md:col-span-7">
      <h1 class="text-t1 mb-4">{{ contracts.length == 0 ? 'Fakturor' : 'Avtal och fakturor' }}</h1>

      <div class="flex flex-col">
        <h2 class="text-t4 py-6" v-if="contracts.length > 0">Hyresavtal</h2>

        <ContractsList />

        <div class="flex flex-col gap-6 rounded-xl bg-white p-4 ring-1 ring-gray-light md:p-6" v-if="!isSecondaryTenant">
          <h2 v-if="!isLoading" class="text-t4">Mina fakturor</h2>

          <CardLoaderSmall v-if="isLoading" class="flex-1 flex-col !p-0 !ring-0" />

          <div v-else class="flex flex-col gap-10">

            <div
              v-if="!!webapp_config.msg.invoices_info"
              class="mb-4 rounded-[15px] bg-primary-10 p-4"
              v-html="webapp_config.msg.invoices_info"
            ></div>

            <AppendableList v-if="invoices.length > 0" :list="invoices" ky="id" listClass="flex flex-col gap-8">
              <template v-slot:default="{ item, index }">
                <Expandable :expanded="false">
                  <template #default="{ toggle, expanded }">
                    <div
                      class="relative rounded-[8px] bg-gray-10 ring-1 ring-gray-light"
                    >
                      <InvoiceTemplate :invoice="item">
                        <template #body="{ invoice }">
                          <div class="flex items-center justify-between px-3 py-4 md:px-6">
                            <!-- Vänster: Info -->
                            <div class="flex items-center gap-8">
                              <div class="flex">
                                <p class="text-b2 md:text-b1" v-if="invoice.ExpireDate">
                                  {{ formatDate(invoice.ExpireDate, 'YYYY-MM-DD') }}
                                </p>
                              </div>
                              <div class="flex gap-8">
                                <p class="text-b2 md:text-b1">{{ addSpace(invoice.InvoiceAmount) }} kr</p>
                              </div>
                              <!--                            <div class="flex gap-8">
                              <p class="text-b1">Autogiro (FIXA!!)</p>
                            </div>-->
                              <div class="flex hidden gap-8 md:block">
                                <Button v-if="item.PdfLink" variant="secondary" size="small" :href="item.PdfLink">
                                  Visa som pdf
                                  <template #icon-after>
                                    <IconExternal />
                                  </template>
                                </Button>
                              </div>
                            </div>

                            <!-- Status + knapp -->

                            <div class="text-label absolute -top-100 right-6" v-if="!!invoice.Cancelled || !!invoice.CreditInvoice">
                              <span
                                class="px-2 py-1 rounded-sm"
                                :class="'bg-green-light'"
                              >
                                {{ invoice.Cancelled ? 'Makulerad' : 'Kreditfaktura' }}
                              </span>
                            </div>

                            <div class="text-label absolute -top-100 right-6" v-else-if="!!invoice.Credited">
                              <span
                                class="px-2 py-1 rounded-sm"
                                :class="'bg-green-light'"
                              >
                                {{ 'Krediterad' }}
                              </span>
                            </div>

                            <div class="text-label absolute -top-100 right-6" v-else-if="expired(invoice) && unpaid(invoice)">
                              <span
                                class="px-2 py-1 rounded-sm"
                                :class="'text-white bg-wine-red'"
                              >
                                {{ 'Förfallen' }}
                              </span>
                            </div>
                            <div class="text-label absolute -top-100 right-6" v-else>
                              <span
                                class="px-2 py-1 rounded-sm"
                                :class="unpaid(invoice) ? 'bg-primary-30' : 'bg-green-light'"
                              >
                                {{ unpaid(invoice) ? 'Obetald' : 'Betald' }}
                              </span>
                            </div>
                            <Button variant="tertiary" @click="toggle()">
                              <span class="hidden xl:inline">
                                {{ expanded ? 'Dölj detaljer' : 'Visa detaljer' }}
                              </span>

                              <template #icon-after>
                                <!-- Mobile icon -->
                                <span class="xl:hidden">
                                  <component :is="expanded ? 'IconMinus' : 'IconAdd'" />
                                </span>

                                <!-- Desktop icon -->
                                <span class="hidden xl:inline">
                                  <IconChevronDown />
                                </span>
                              </template>
                            </Button>
                          </div>
                        </template>
                      </InvoiceTemplate>
                    </div>
                  </template>

                  <template #body="invoice">
                    <div
                      class="rounded-[15px] rounded-tl-none rounded-tr-none bg-gray-10 px-4 py-4 ring-1 ring-gray-light md:px-6 md:py-6 z-20 -mt-[4px] relative"
                    >
                      <div class="text-base flex grid-cols-2 flex-col gap-x-8 gap-y-4 md:grid">
                        <div class="flex flex-col gap-4">
                          <h3 class="text-t6">Kunduppgifter</h3>
                          <div class="flex">
                            <p class="text-b2">Kundnummer:</p>
                            <p class="text-b2 ml-2">{{ item.CustomerCode }}</p>
                          </div>

                          <div class="flex">
                            <p class="text-b2">Namn:</p>
                            <p class="text-b2 ml-2">
                              {{ item.TenantName }}
                            </p>
                          </div>

                          <div class="flex">
                            <p class="text-b2">Adress:</p>
                            <p class="text-b2 ml-2">{{ item.Address }}</p>
                          </div>
                        </div>

                        <div class="flex flex-col gap-4">
                          <h3 class="text-t6">Detaljspecifikation</h3>
                          <div class="flex">
                            <p class="text-b2">Faktura/OCR:</p>
                            <p class="text-b2 ml-2">{{ item.Ocr }}</p>
                          </div>

                          <div class="flex">
                            <p class="text-b2">Period:</p>
                            <p class="text-b2 ml-2" v-if="item.FromDate">
                              {{ formatDate(item.FromDate, 'YYYY-MM-DD') }} -
                              {{ formatDate(item.ToDate, 'YYYY-MM-DD') }}
                            </p>
                          </div>

                          <div class="flex">
                            <p class="text-b2">Bankgiro:</p>
                            <p class="text-b2 ml-2">336-7133</p>
                          </div>

                          <div class="flex gap-8 md:hidden">
                            <Button v-if="item.PdfLink" variant="secondary" size="small" :href="item.PdfLink">
                              Visa som pdf
                              <template #icon-after>
                                <IconExternal />
                              </template>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </Expandable>
              </template>
            </AppendableList>

            <div v-else>Du har inte några fakturor</div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import * as config from '@/config';
import { CONTRACT_STATUSES } from '@/config/constants';
import Box from '@/components/ui/box';
import Button from '@/components/ui/button';
import IconExternal from '@/components/icons/IconExternal';
import IconAdd from '@/components/icons/IconAdd';
import IconMinus from '@/components/icons/IconMinus';
import Expandable from '@/components/ui/expandable';
import Modal from '@/components/ui/modal';
import RentalObjectTemplate from '@/templates/rentalobjecttemplate';
import AppendableList from '@/components/ui/appendable-list';
import { RENTALOBJECT_TYPES, RENTALOBJECT_TYPE_CODES } from '@/config/constants';
import InvoiceTemplate from '@/templates/invoicetemplate';
import ContractsList from '@/components/profile/contractslist';

import IconArrowRight from '@/components/icons/IconArrowRight';
import IconChevronDown from '@/components/icons/IconChevronDown';

import GeneralMixin from '@/mixins/general';
import MixinsInvoice from '@/mixins/invoice';
import _ from 'lodash';
import CardLoaderSmall from '@/components/loader/CardLoaderSmall';

export default {
  name: 'ContractsInvoices',
  mixins: [GeneralMixin, MixinsInvoice],
  components: {
    Box,
    Button,
    IconExternal,
    Expandable,
    Modal,
    RentalObjectTemplate,
    AppendableList,
    InvoiceTemplate,
    IconAdd,
    IconMinus,
    ContractsList,
    IconArrowRight,
    IconChevronDown,
    CardLoaderSmall,
  },
  data() {
    return {
      showLoader: true,
      isLoading: true,
      // testAlwaysLoading: true,
      CONTRACT_STATUSES,
      showPolicy: false,
      apiUrl: config.API_BASEURL_V2,
      TerminateContractModal: false,
      terminationSteps: [
        {
          title: 'Säga upp avtal',
          nextButtonText: 'Ja, säg upp avtalet',
          backButtonText: 'Nej, avbryt',
        },
        {
          title: 'När vill du flytta?',
          nextButtonText: 'Nästa',
          backButtonText: 'Tillbaka',
        },
        {
          title: 'Stämmer dina kontaktuppgifter?',
          nextButtonText: 'Nästa',
          backButtonText: 'Tillbaka',
        },
        {
          title: 'Signera din uppsägning',
          nextButtonText: 'Ok, skicka e-postmeddelande',
          backButtonText: 'Tillbaka',
        },
        {
          title: 'Tidsbegränsad länk är skickad till din e-postadress',
          confirmButtonText: 'Gå till checklistan',
          backButtonText: 'Tillbaka',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      webapp_config: 'webapp_config',
    }),

    contracts() {
      return this.$store.getters.contracts;
    },

    isSecondaryTenant() {
      return !!this.$store.getters.claims.some((claim) => claim == 'secondaryRentalContract');
    },

    invoices() {
      return this.$store.getters.invoices; //orderBy(this.$store.getters.invoices, 'ExpireDate', 'desc');
    },
  },
  methods: {
    handleTerminationComplete() {
      // Handle the completion of the termination process
      this.TerminateContractModal = false;
      // Navigate to checklist or perform other actions
    },

    typeCaption(item) {
      if (item.rentalObjectType == RENTALOBJECT_TYPES.APARTMENT) return 'Bostad';
      if (item.rentalObjectType == RENTALOBJECT_TYPES.PARKING) return 'Fordonsplatser';
      if (item.rentalObjectType == RENTALOBJECT_TYPES.PREMISE) return 'Förråd';
      return;
    },

    cancelPolicy(item) {
      if (item) {
        return item.cancelPolicyTenant.quantity + "" + item.cancelPolicyTenant.unit;
      }
    },
    showContractTermination(contract) {
      // Funktionnen måste vara aktiverad

      if (!this.$store.getters.webapp_config.allow_contract_termination || !!this.isSecondaryTenant)
        return false;

      if (!contract) return false;

      let isPerson = this.$store.getters.claims.some((claim) => claim == 'person');
      let isRightKindOfType = ![
        RENTALOBJECT_TYPE_CODES.LOKAL_TYP_1,
        RENTALOBJECT_TYPE_CODES.LOKAL_TYP_2,
        RENTALOBJECT_TYPE_CODES.LOKAL_TYP_4,
        RENTALOBJECT_TYPE_CODES.OVRIGT_TYP_1,
      ].some((code) => code == contract.rentalObject.rentalObjectTypeCode);
      let isValid = contract.status == CONTRACT_STATUSES.VALID;

      return !this.isSecondaryTenant && isPerson && isRightKindOfType && isValid;
    },

    blueprint(contract) {
      if (!(contract && contract.rentalObject.images && contract.rentalObject.images.length > 0))
        return;

      const img = contract.rentalObject.images.find((image) => image.subTypeCaption == 'Ritning');

      if (img) return img.url;
    },
  },
  created() {
    if (!this.testAlwaysLoading) {
      return Promise.all([
        this.$store.cache.dispatch('fetchInvoices'),
        this.$store.cache.dispatch('fetchContracts'),
      ]).finally(() => {
        this.showLoader = false;
        this.isLoading = false;
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.rotate-180 {
  transform: rotate(180deg);
}
</style>
